/**
 * ⑨ REPORTS & EXPORT — WITH DRILL-DOWN
 * UC-18: ReportAssembly, UC-19: CDPResponse, UC-20: XBRLExport
 */
import { useState, useEffect } from "react";
import { C, api, post, patch, Btn, Card, Label, Val, Input, Badge, Err, MetricBar, Loader,
         Breadcrumb, DrillRow, DetailPanel, TabBar, HistoryTab, EmptyState, KPICard, LinkedEntity } from "./shared";

// ═══ UC-18: REPORT ASSEMBLY ═══
export function ReportAssembly() {
  const [report,setReport]=useState(null); const [loading,setLoading]=useState(false);
  const [selSec,setSelSec]=useState(null); const [tab,setTab]=useState("Overview");
  const assemble=async()=>{setLoading(true);try{setReport(await post("/reports/assemble?fiscal_year=2024",{}));}catch(e){}setLoading(false);};
  useEffect(()=>{assemble();},[]);
  return (<div data-testid="report-assembly-panel">
    <Breadcrumb path={[{label:"CSRD Report",onClick:()=>setSelSec(null)},...(selSec?[{label:selSec.standard||selSec.section_id}]:[])]}/>
    {!selSec&&report&&<><div style={{display:"flex",gap:8,marginBottom:12}}>
      <KPICard label="Sections" value={report.sections?.length||0} color={C.blue}/>
      <KPICard label="Datapoints" value={report.total_datapoints||0} color={C.cyan}/>
      <KPICard label="Coverage" value={`${report.coverage_pct||0}%`} color={(report.coverage_pct||0)>50?C.emerald:C.red}/></div>
      {(report.sections||[]).map((s,i)=>(<DrillRow key={i} onClick={()=>{setSelSec(s);setTab("Overview");}}
        cols={[<span style={{fontWeight:600,color:C.teal}}>{s.standard||s.section_id}</span>,
          `${s.datapoints||0} DPs`,
          <Badge text={`${s.coverage_pct||0}%`} color={(s.coverage_pct||0)>50?C.emerald:C.amber}/>]}/>))}</>}

    {selSec&&<DetailPanel visible onClose={()=>setSelSec(null)} title={selSec.standard||selSec.section_id} color={C.teal}>
      <TabBar tabs={["Overview","Datapoints","Claims","History"]} active={tab} onChange={setTab}/>
      {tab==="Overview"&&<div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
        <div><Label>Datapoints</Label><Val>{selSec.datapoints||0}</Val></div>
        <div><Label>Covered</Label><Val color={C.emerald}>{selSec.covered||0}</Val></div>
        <div><Label>Gaps</Label><Val color={C.red}>{(selSec.datapoints||0)-(selSec.covered||0)}</Val></div></div></div>}
      {tab==="Datapoints"&&<div style={{color:C.muted,fontSize:11}}>Each datapoint with status (covered/gap), linked claim text for covered items, and action button for gaps. Requires per-section endpoint (planned).</div>}
      {tab==="Claims"&&<div>{(selSec.claims||[]).map((cl,i)=>(
        <DrillRow key={i} onClick={()=>{}} cols={[cl.claim_id||"—",(cl.text||"").slice(0,60),<Badge text={cl.status||"draft"} color={C.emerald}/>]}/>))
        }{(!selSec.claims||selSec.claims.length===0)&&<div style={{color:C.muted,fontSize:11}}>No claims linked to this section yet.</div>}</div>}
      {tab==="History"&&<HistoryTab resourceId={selSec.section_id||selSec.standard}/>}
    </DetailPanel>}
  </div>);
}

// ═══ UC-19: CDP RESPONSE ═══
export function CDPResponse() {
  const [cdp,setCdp]=useState(null); const [loading,setLoading]=useState(false);
  const [sel,setSel]=useState(null);
  const gen=async()=>{setLoading(true);try{setCdp(await post("/reports/cdp/generate",{}));}catch(e){}setLoading(false);};
  useEffect(()=>{api("/reports/cdp/mappings").then(setCdp).catch(()=>{});},[]);
  return (<div data-testid="cdp-response-panel">
    <Breadcrumb path={[{label:"CDP Response",onClick:()=>setSel(null)},...(sel?[{label:sel.cdp_question||sel.question_id}]:[])]}/>
    {!sel&&<>{cdp&&<div style={{display:"flex",gap:8,marginBottom:12}}>
      <KPICard label="Total Questions" value={cdp.total_questions||cdp.total||0} color={C.blue}/>
      <KPICard label="Auto-filled" value={cdp.auto_filled||0} color={C.emerald}/></div>}
      {(cdp?.mappings||cdp?.questions||[]).map((m,i)=>(<DrillRow key={i} onClick={()=>setSel(m)}
        cols={[<span style={{fontWeight:600}}>{m.cdp_question||m.question_id||`Q${i+1}`}</span>,
          <span style={{color:C.muted}}>{(m.esrs_source||m.mapped_from||"—").slice(0,30)}</span>,
          <Badge text={m.auto_filled?"Auto":"Manual"} color={m.auto_filled?C.emerald:C.amber}/>]}/>))}
      <Btn onClick={gen} color={C.orange} disabled={loading}>{loading?"...":"Generate CDP"}</Btn></>}
    {sel&&<DetailPanel visible onClose={()=>setSel(null)} title={sel.cdp_question||sel.question_id} color={C.orange}>
      <Label>CDP Question</Label><div style={{fontSize:11,marginBottom:8}}>{sel.cdp_question||sel.question_id}</div>
      <Label>ESRS Source</Label><div style={{fontSize:11}}>{sel.esrs_source||sel.mapped_from||"—"}</div>
      <Label>Auto-filled</Label><Badge text={sel.auto_filled?"Yes":"No"} color={sel.auto_filled?C.emerald:C.amber}/>
    </DetailPanel>}
  </div>);
}

// ═══ UC-20: XBRL EXPORT ═══
export function XBRLExport() {
  const [result,setResult]=useState(null); const [loading,setLoading]=useState(false);
  const gen=async()=>{setLoading(true);try{setResult(await post("/reports/xbrl?fiscal_year=2024",{}));}catch(e){}setLoading(false);};
  return (<div data-testid="xbrl-export-panel"><Btn onClick={gen} color={C.blue} disabled={loading}>{loading?"Generating...":"Generate XBRL"}</Btn>
    {result&&<Card color={C.blue} style={{marginTop:12}}>
      <div style={{display:"flex",gap:12}}><div><Label>Format</Label><Val>{result.format}</Val></div>
        <div><Label>Facts</Label><Val>{result.fact_count||0}</Val></div></div>
      {result.preview&&<pre style={{fontSize:9,padding:8,background:C.card,borderRadius:4,overflow:"auto",maxHeight:200,marginTop:8,color:C.cyan}}>{result.preview}</pre>}</Card>}
  </div>);
}
